import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import FoldedHandsSvg from '../../../../assets/icons/FoldedHandsSvg';
import LinkSvg from '../../../../assets/icons/LinkSvg';
import theme from '../../../../assets/theme';
import { usePrevious } from '../../../../hooks/usePrevious';
import {
  getNrkIdFromNrkUrl,
  isNrkUrl,
  parsePastedVideoEmbedUrl,
} from '../../../../utils/parsePastedVideoEmbedUrl';
import { VIDEO_ASPECT_RATIO } from '../../../../utils/templateUtils';
import { Button } from '../../../_atoms/Button';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import MaxSizeMediaContent from '../../../_molecules/MaxSizeMediaContent';
import NrkViewer from '../../../_molecules/NrkViewer';
import SearchInput from '../../../_molecules/SearchInput';
import {
  MediaMimeType,
  OnElementsChangeProps,
  Slide,
  SlideElement,
} from '../../../SlideComponents/slide.types';
import { createIframeElement } from '../../../SlideComponents/slideUtil';
import { appSmashes, IframeAppSmash } from './AppSmashUtils';

const EmbeddedUrlViewer = ({ embedUrl }: { embedUrl: string }) => {
  if (embedUrl === undefined) return null;

  if (embedUrl.length > 0) {
    if (isNrkUrl(embedUrl)) {
      return <NrkViewer disabledMedia={false} nrkId={getNrkIdFromNrkUrl(embedUrl)} />;
    }
    return (
      <iframe
        src={embedUrl}
        style={{
          borderRadius: '10px',
          border: 'none',
          margin: '0 auto',
        }}
        width="100%"
        height="100%"
        title="Video player"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      ></iframe>
    );
  }
  return null;
};

const AppSmashPicker = ({
  onRoundChange,
  selectedElement,
  round,
  iframeType,
  onElementsChange,
}: {
  round: Slide;
  onRoundChange: (round: Slide) => void;
  onElementsChange: (props: OnElementsChangeProps) => void;
  selectedElement: SlideElement | null;
  iframeType?: IframeAppSmash;
}) => {
  const [pastedUrl, setPastedUrl] = useState<string>('');
  const [parsedUrl, setParsedUrl] = useState<string | undefined>();
  const [videoData, setVideoData] = useState<{ src: string; mimeType: string }>();
  const previousRound = usePrevious(round);

  const resetVideoState = useCallback(() => {
    setPastedUrl('');
    setParsedUrl('');
    setVideoData(undefined);
  }, []);

  useEffect(() => {
    if (round.id !== previousRound?.id) {
      resetVideoState();
    }
  }, [round, previousRound, resetVideoState]);

  useEffect(() => {
    if (selectedElement?.type === 'iframe') {
      setParsedUrl(selectedElement.mediaUrl);
    }
  }, [selectedElement]);

  const handlePastedUrlChange = (pastedUrl: string) => {
    setPastedUrl(pastedUrl);
    parsePastedVideoEmbedUrl(pastedUrl).then(parsedUrl => {
      if (parsedUrl) {
        setParsedUrl(parsedUrl);
        setVideoData({
          src: parsedUrl,
          mimeType: 'iframe/video',
        });
      } else {
      }
    });
  };

  const handleURLUpload = ({
    videoId,
    mimeType,
    altText,
  }: {
    videoId: string;
    mimeType: MediaMimeType;
    altText?: string;
  }) => {
    if (selectedElement) {
      const updatedElement: SlideElement = {
        id: selectedElement.id,
        angle: selectedElement.angle,
        x: selectedElement.x,
        y: selectedElement.y,
        version: 1,
        width: selectedElement.width,
        height: selectedElement.height,
        borderRadius: selectedElement.borderRadius,
        opacity: selectedElement.opacity,
        type: 'iframe',
        iframeOrigin: iframeType,
        mediaUrl: videoId || selectedElement.mediaUrl,
        mediaMimetype: mimeType || selectedElement.mediaMimetype,
        mediaAltText: altText || selectedElement.mediaAltText,
      };
      onElementsChange({ updatedElements: [updatedElement] });
      return;
    }
    const newElement = createIframeElement({
      url: videoId,
      altText: altText,
      iframeOrigin: iframeType,
    });

    onRoundChange({
      ...round,
      elements: [...round.elements, newElement],
    });

    return;
  };

  const handleSubmit = () => {
    if (videoData) {
      handleURLUpload({
        videoId: videoData.src,
        mimeType: videoData.mimeType as MediaMimeType,
      });
    }
  };
  const handleClear = () => {
    resetVideoState();
  };

  const handleClickEnter = () => {
    handleSubmit();
  };

  const { t } = useTranslation('Host');
  return (
    <CenteredDiv>
      <div style={{ padding: '0px 20px' }}>
        <SearchInput
          value={pastedUrl}
          className="prevent-unselect-slide-element"
          placeholder={
            iframeType === 'other'
              ? t('Paste link here')
              : `${t('Paste the link from')} ${iframeType}`
          }
          onChange={handlePastedUrlChange}
          icon={<LinkSvg />}
          onPressEnter={handleClickEnter}
          style={{ width: '100%' }}
        />
      </div>
      <div style={{ width: '100%', height: 'fit-content', padding: '0px 20px' }}>
        {parsedUrl && (
          <MaxSizeMediaContent aspectRatio={VIDEO_ASPECT_RATIO}>
            <EmbeddedUrlViewer embedUrl={parsedUrl} />
          </MaxSizeMediaContent>
        )}
        {!pastedUrl && !parsedUrl && (
          <>{appSmashes.find(app => app.id === iframeType)?.instructions}</>
        )}
      </div>
      {!parsedUrl && pastedUrl && (
        <div
          style={{
            padding: '0px 20px',
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          <IconWrapper style={{ cursor: 'default' }} height="150px" width="150px">
            <FoldedHandsSvg />
          </IconWrapper>
          <p style={{ width: '100%', fontWeight: 'bold', textAlign: 'center' }}>
            {t(`I'm sorry but we could not recognise this url. Please try another url`)}
          </p>
        </div>
      )}
      {parsedUrl && (
        <>
          <BottomActionbarContainer>
            <ActionBarResponsiveButtonContainer style={{ marginRight: 10 }}>
              <Button
                styles={{
                  flex: 0,
                  color: theme.colors.black,
                  borderColor: theme.colors.black,
                }}
                height="42px"
                width="88px"
                type="tertiary"
                onClick={() => {
                  handleClear();
                }}
              >
                {t('Clear')}
              </Button>
            </ActionBarResponsiveButtonContainer>

            <ActionBarResponsiveButtonContainer style={{ marginLeft: 10 }}>
              <Button
                styles={{ flex: 0 }}
                height="42px"
                width="88px"
                type="primary"
                onClick={() => {
                  handleSubmit();
                }}
              >
                {t('Apply')}
              </Button>
            </ActionBarResponsiveButtonContainer>
          </BottomActionbarContainer>
        </>
      )}
    </CenteredDiv>
  );
};

const ActionBarResponsiveButtonContainer = styled.div`
  flex: 0;
  @media (max-width: 500px) {
    order: 1;
    margin-top: 10px;
  }
`;

const BottomActionbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
  padding: 0px 20px;
  flex: 0;

  @media (max-width: 600px) {
    padding-left: 0px;
    padding-right: 0px;
  }
  @media (max-width: 500px) {
    flex-wrap: wrap;
  }
`;

export default AppSmashPicker;

const CenteredDiv = styled.div`
  width: 100%;
  height: 100%;
`;
